import { Fragment, Children, isValidElement, ReactNode, SelectHTMLAttributes, useLayoutEffect, useMemo, useRef } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import FaIcon from './FaIcon';

// =============================================================================
// SELECT COMPONENT
// =============================================================================
// A themed replacement for the native <select> popup.
//
// The closed control could always be styled; the list that drops out of it
// could not. That list is drawn by the operating system, so it arrived in the
// platform's own colours and metrics -- a white menu with 13px system type over
// a dark console, different again between Windows, macOS and Android. No amount
// of CSS reaches it.
//
// So the list is ours: a Listbox, themed from the same tokens as everything
// else, keyboard-navigable, and anchored in a portal so it is not clipped by
// the scrolling panel or modal it happens to live in.
//
// It takes <option> and <optgroup> children, exactly as a native select does,
// so the several hundred existing call sites did not have to change. Passing
// `multiple` opts back out to the native element, which handles multi-select
// better than a re-implementation would.

export type SelectVariant = 'default' | 'error' | 'success';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  variant?: SelectVariant;
  fullWidth?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

interface Item {
  value: string;
  label: string;
  disabled: boolean;
  group?: string;
}

/**
 * Flatten <option> / <optgroup> children into a list.
 *
 * Children arrive from `.map()` calls, fragments and conditionals, so this
 * walks arrays and fragments rather than assuming a flat list of options.
 */
function readOptions(children: ReactNode, group?: string): Item[] {
  return Children.toArray(children).flatMap((child): Item[] => {
    if (!isValidElement(child)) return [];
    const props = child.props as { value?: unknown; children?: ReactNode; disabled?: boolean; label?: string };
    if (child.type === Fragment) return readOptions(props.children, group);
    if (child.type === 'optgroup') return readOptions(props.children, props.label);
    if (child.type !== 'option') return [];
    const value = props.value === undefined ? '' : String(props.value);
    const label = typeof props.children === 'string' || typeof props.children === 'number'
      ? String(props.children)
      : Children.toArray(props.children).filter((c) => typeof c === 'string' || typeof c === 'number').join('') || value;
    return [{ value, label, disabled: Boolean(props.disabled), group }];
  });
}

const VARIANT_BORDER: Record<SelectVariant, string> = {
  default: [
    'border-theme-secondary',
    'hover:border-gray-400 dark:hover:border-gray-500',
    'data-[open]:border-primary-500 data-[open]:ring-2 data-[open]:ring-primary-500/30',
    'dark:data-[open]:border-primary-400 dark:data-[open]:ring-primary-400/20',
    'focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500/30',
    'dark:focus-visible:border-primary-400 dark:focus-visible:ring-primary-400/20',
  ].join(' '),
  error: [
    'border-red-400 dark:border-red-500',
    'hover:border-red-500 dark:hover:border-red-400',
    'focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/30',
    'dark:focus-visible:border-red-400 dark:focus-visible:ring-red-400/20',
  ].join(' '),
  success: [
    'border-green-400 dark:border-green-500',
    'hover:border-green-500 dark:hover:border-green-400',
    'focus-visible:border-green-500 focus-visible:ring-2 focus-visible:ring-green-500/30',
    'dark:focus-visible:border-green-400 dark:focus-visible:ring-green-400/20',
  ].join(' '),
};

const BUTTON_BASE = [
  'group',
  'flex items-center justify-between gap-2',
  // Height comes from padding, as it did on the native select. A min-height
  // here would grow the dense selects -- map toolbars, table filters -- that
  // pass their own `text-xs py-1.5`.
  'px-3 py-2',
  'border rounded-lg',
  'bg-white dark:bg-gray-800/50',
  'text-theme-primary text-sm text-left',
  'transition-all duration-200',
  'focus:outline-none',
  'disabled:opacity-60 disabled:cursor-not-allowed',
  'disabled:bg-gray-100 dark:disabled:bg-gray-800/30',
].join(' ');

export default function Select({
  variant = 'default',
  fullWidth = true,
  className = '',
  children,
  value,
  defaultValue,
  onChange,
  name,
  id,
  disabled,
  required,
  multiple,
  onBlur,
  title,
  autoFocus,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...rest
}: SelectProps) {
  const items = useMemo(() => readOptions(children), [children]);

  /*
   * Name the button from the field's own <label>.
   *
   * The label's `for` points at the hidden native select, because that is where
   * the value lives and what tests and Playwright drive. The button is what
   * assistive technology actually sees, so it needs the same name -- and
   * Headless UI computes `aria-labelledby` itself and drops the one passed as a
   * prop. Setting it on the node afterwards is the way through: `aria-label`
   * survives the prop path but would duplicate the label text rather than
   * pointing at it.
   */
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const button = buttonRef.current;
    if (!button || !id || ariaLabel || ariaLabelledBy) return;
    if (document.getElementById(`${id}-label`)) button.setAttribute('aria-labelledby', `${id}-label`);
  }, [id, ariaLabel, ariaLabelledBy]);

  // Multi-select is rare here (one call site) and the native control does it
  // better than a re-implementation would, so it keeps the native element.
  if (multiple) {
    return (
      <select
        className={`${BUTTON_BASE} ${VARIANT_BORDER[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        name={name}
        id={id}
        disabled={disabled}
        required={required}
        onBlur={onBlur}
        title={title}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        multiple
        {...rest}
      >
        {children}
      </select>
    );
  }

  const current = value === undefined || value === null ? '' : String(value);
  const selected = items.find((item) => item.value === current);

  // Existing handlers read `event.target.value`; give them exactly that shape
  // rather than asking several hundred call sites to change.
  const emit = (next: string) => {
    const target = { value: next, name: name ?? '', id: id ?? '' };
    onChange?.({ target, currentTarget: target } as unknown as React.ChangeEvent<HTMLSelectElement>);
  };

  const groups = items.reduce<Array<{ group?: string; items: Item[] }>>((acc, item) => {
    const last = acc[acc.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else acc.push({ group: item.group, items: [item] });
    return acc;
  }, []);

  return (
    <Listbox value={current} onChange={emit} disabled={disabled}>
      <div className={`relative ${fullWidth ? 'w-full' : 'inline-block'}`}>
        {/*
          A hidden native select keeps what the visible control cannot provide
          on its own: the value posts with a plain form submit, `required` still
          participates in native validation, and both Testing Library and
          Playwright can still drive the field by changing a real <select>.

          It carries `${id}-native` and `data-select-for` rather than the plain
          `id`, because the id belongs on the control the user actually
          operates -- that is what the <label for> points at.
        */}
        <select
          id={id}
          data-select-for={id || name || ariaLabel}
          name={name}
          value={current}
          required={required}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
          onChange={(e) => emit(e.target.value)}
          className="sr-only"
        >
          {items.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>

        {/*
          Only the accessibility props are forwarded to the button. `rest` is
          typed for a <select> -- spreading it here hands a
          MouseEventHandler<HTMLSelectElement> to a <button>, which is both a
          type error and, for anything that fires, wrong.
        */}
        <ListboxButton
          ref={buttonRef}
          onBlur={onBlur as unknown as React.FocusEventHandler<HTMLButtonElement>}
          title={title}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : ariaLabelledBy ?? (id ? `${id}-label` : undefined)}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired ?? (required ? true : undefined)}
          className={`${BUTTON_BASE} ${VARIANT_BORDER[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
        >
          {/*
            * A value none of the options carry renders as EMPTY, not as the
            * first option's label.
            *
            * `items[0]?.label` mimicked what a native <select> does with an
            * unmatched value, and it is a lie every time it fires: the control
            * displays a choice the record does not hold. The IPAWS composer
            * showed Urgency "Immediate" on an alert carrying no urgency, and a
            * call with no disposition read as "Report Logged" -- a
            * classification nobody had made. Whether that state is reachable at
            * all is a call-site question; what this must not do is hide it.
            */}
          <span className={`truncate ${selected && selected.value !== '' ? '' : 'text-gray-400 dark:text-gray-500'}`}>
            {selected?.label ?? ''}
          </span>
          <FaIcon
            icon="chevron-down"
            className="h-3.5 w-3.5 shrink-0 text-theme-tertiary transition-transform duration-200 group-data-[open]:rotate-180"
          />
        </ListboxButton>

        <ListboxOptions
          anchor="bottom start"
          transition
          className={[
            'z-[60] w-[var(--button-width)] max-h-72 overflow-auto',
            'mt-1 rounded-lg border border-theme-primary',
            'bg-surface-elevated shadow-lg',
            'py-1 text-sm',
            'focus:outline-none',
            'transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:-translate-y-1',
          ].join(' ')}
        >
          {groups.map((group, groupIndex) => (
            <Fragment key={group.group ?? `group-${groupIndex}`}>
              {group.group && (
                <div className="px-3 pt-2 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-theme-tertiary">
                  {group.group}
                </div>
              )}
              {group.items.map((item) => (
                <ListboxOption
                  key={`${group.group ?? ''}:${item.value}`}
                  value={item.value}
                  disabled={item.disabled}
                  className={[
                    'group flex items-center justify-between gap-2 px-3 py-2 cursor-pointer',
                    // 36px is fine under a mouse. On a touchscreen it is under
                    // the 44px this app holds itself to, and the option list is
                    // new UI with no layout depending on its height -- unlike
                    // the button, which keeps the height the native select had.
                    '[@media(pointer:coarse)]:min-h-[2.75rem]',
                    'text-theme-primary',
                    // The keyboard-focused row has to be obvious. surface-tertiary
                    // over surface-elevated is a 4-value difference in the dark
                    // palette -- present in the DOM, invisible on the screen.
                    'data-[focus]:bg-primary-50 dark:data-[focus]:bg-primary-400/15',
                    'data-[focus]:text-primary-900 dark:data-[focus]:text-theme-primary',
                    // A placeholder is not a choice. `<option value="">Select a
                    // type...</option>` is the current value when nothing has
                    // been picked, so data-[selected] bolds it and puts a tick
                    // beside it -- reading as though the operator had chosen
                    // "Select a type...". Keep it muted and unticked.
                    item.value === '' ? 'text-theme-tertiary' : 'data-[selected]:font-medium',
                    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                  ].join(' ')}
                >
                  <span className="truncate">{item.label}</span>
                  {item.value !== '' && (
                    <FaIcon
                      icon="check"
                      className="h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary-400 opacity-0 group-data-[selected]:opacity-100"
                    />
                  )}
                </ListboxOption>
              ))}
            </Fragment>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}
